import { inject, Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { DashboardProfileContextService } from '@rma/authentication/public/user';
import { isIndividual, isLeasingVertical, isOffice, isSalesVertical } from '@rma/common-ui-domain/helpers';
import { VerticalType } from '@rma/common-ui-domain/types';
import { FeatureFlags, FeatureFlagService } from '@rma/generic/util/feature-flags';
import { rmaShareReplay } from '@rma/generic/util/operators/rma-share-replay';
import { getVerticalType } from '@rma/generic/util/router-utils';
import { type GoogleConnectionStatus, GooglePromptStatus, GoogleUserContextService } from '@rma/google/public/google-user-context';
import { GoogleConversionService } from '@rma/google/public/util-google-conversion';
import { ConnectionStatus, ConnectToZillowService } from '@rma/linked-accounts/public/api-connect-zillow';
import { ExternalReviewsContextService, ExternalReviewSourceType } from '@rma/reviews/public/api-external-reviews';
import { combineLatest, map, Observable, of, switchMap } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class ExternalProviderStatusService {
  private readonly connectToZillowService = inject(ConnectToZillowService);
  private readonly externalReviewsContextService = inject(ExternalReviewsContextService);
  private readonly featuresService = inject(FeatureFlagService);
  private readonly googleConversionService = inject(GoogleConversionService);
  private readonly googleUserContextService = inject(GoogleUserContextService);
  private readonly profileContextService = inject(DashboardProfileContextService);
  private readonly route = inject(ActivatedRoute);

  public readonly bellePropertyImportCount$: Observable<number>;
  public readonly externalReviewsEnabled$: Observable<boolean>;
  public readonly externalSources$: Observable<ExternalReviewSourceType[]>;
  public readonly googleConnected$: Observable<boolean>;
  public readonly googleConnectionStatus$: Observable<GoogleConnectionStatus>;
  public readonly googlePromptStatus$: Observable<GooglePromptStatus>;
  public readonly zillowConnected$: Observable<boolean>;

  constructor() {
    const vertical$ = this.route.queryParamMap.pipe(map((params): VerticalType => getVerticalType(params)));

    const enabledForProfile$ = combineLatest([vertical$, this.profileContextService.profileType$]).pipe(
      map(([v, p]) => (isIndividual(p) && isSalesVertical(v)) || (isOffice(p) && isLeasingVertical(v))),
    );

    this.externalReviewsEnabled$ = combineLatest([
      this.featuresService.getFeature$(FeatureFlags.DashboardEnableLinkReviewsNavigation, false),
      enabledForProfile$,
    ]).pipe(map(([enableLinkReviewsNavigation, enabledForProfile]) => enableLinkReviewsNavigation && enabledForProfile));

    this.googleConnectionStatus$ = this.googleUserContextService.googleConnectionStatus$;
    this.googleConnected$ = this.googleConnectionStatus$.pipe(map((status) => status === 'connected'));
    this.googlePromptStatus$ = this.getGooglePromptStatus();

    this.zillowConnected$ = this.connectToZillowService
      .getConnectToZillowSettings()
      .pipe(map((settings) => (settings?.connectionStatus ?? ConnectionStatus.Disconnected) === ConnectionStatus.Connected));

    // This is for Belle Property Group
    this.bellePropertyImportCount$ = combineLatest({
      vertical: vertical$,
      externalReviewsEnabled: this.externalReviewsEnabled$,
      profile: this.profileContextService.profileIdentifier$.pipe(map(({ profileType }) => ({ profileType }))),
    }).pipe(
      switchMap(({ vertical, externalReviewsEnabled, profile: { profileType } }) => {
        const isSalesIndividual = isIndividual(profileType) && isSalesVertical(vertical);
        const isLeasingOffice = isOffice(profileType) && isLeasingVertical(vertical);
        const hasAccessToImport = !externalReviewsEnabled && (isSalesIndividual || isLeasingOffice);
        return hasAccessToImport ? this.externalReviewsContextService.getOnlyImportableReviewCount(vertical, ['Birdeye']) : of(0);
      }),
    );

    this.externalSources$ = vertical$.pipe(
      switchMap((v) => this.externalReviewsContextService.getAvailableSources(v)),
      rmaShareReplay(),
    );
  }

  private getGooglePromptStatus(): Observable<GooglePromptStatus> {
    const hasAccess$ = combineLatest({
      hasAccessViaOwner: this.googleUserContextService.hasOwnGoogleAskForReviews$,
      hasAccessViaSelf: this.googleUserContextService.hasGoogleAskForReviews$,
    }).pipe(map(({ hasAccessViaOwner, hasAccessViaSelf }) => hasAccessViaOwner || hasAccessViaSelf));

    const hasGoogleConnected$ = combineLatest({
      connected: this.googleConnected$,
      convertReviewDestination: this.googleConversionService.salesGoogleConvertReviewDestination$,
    }).pipe(map(({ connected, convertReviewDestination }) => connected && !!convertReviewDestination?.currentDestination));

    return combineLatest({ hasAccess: hasAccess$, connected: hasGoogleConnected$ }).pipe(
      map(({ hasAccess, connected }) => {
        if (!hasAccess) {
          return 'upgrade';
        }
        if (!connected) {
          return 'connect';
        }
        return 'allowed';
      }),
    );
  }
}
